import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Maximize2, Minimize2, Minus, Plus, X } from "lucide-react";
import {
  OpsImageArtifactProjectionSchema,
  OpsImageJobProjectionSchema,
  type OpsImageArtifactProjection,
  type OpsImageJobProjection
} from "@business-ops/domain";
import { installDrawerFocusBoundary } from "./ui.js";
import "./ops-image-artifact.css";

const IMAGE_PROVIDER_LABELS = {
  OPENAI_IMAGE_API: "OpenAI Image API",
  CODEX_IMAGE: "Codex Image",
  LOCAL_UPSCALER: "Local upscaler"
} satisfies Record<OpsImageArtifactProjection["provider"], string>;

type PreviewWindow = Pick<Window, "history" | "location" | "addEventListener" | "removeEventListener">;

export function opsImageProviderLabel(provider: OpsImageArtifactProjection["provider"]): string {
  return IMAGE_PROVIDER_LABELS[provider];
}

export function opsImageContentUrl(artifactId: string, download = false): string {
  const id = OpsImageArtifactProjectionSchema.shape.artifactId.parse(artifactId);
  const base = `/api/ops-assistant/images/${encodeURIComponent(id)}/content`;
  return download ? `${base}?download=1` : base;
}

export function installOpsImagePreviewLifecycle(
  panel: HTMLElement | null,
  artifactId: string,
  onClose: () => void,
  ownerWindow: PreviewWindow = window,
  ownerDocument: Document = document
): { requestClose: () => void; dispose: () => void } {
  let historyActive = true;
  let closeRequested = false;
  let disposed = false;
  const closeFromHistory = () => {
    historyActive = false;
    closeRequested = false;
    onClose();
  };
  ownerWindow.history.pushState(
    { ...(ownerWindow.history.state ?? {}), opsImageArtifact: artifactId },
    "",
    ownerWindow.location.href
  );
  ownerWindow.addEventListener("popstate", closeFromHistory);
  const requestClose = () => {
    if (!historyActive) {
      onClose();
      return;
    }
    if (closeRequested) return;
    closeRequested = true;
    ownerWindow.history.back();
  };
  const releaseFocus = installDrawerFocusBoundary(panel, requestClose, ownerDocument);
  return {
    requestClose,
    dispose: () => {
      if (disposed) return;
      disposed = true;
      ownerWindow.removeEventListener("popstate", closeFromHistory);
      // Private mode and parent unmounts remove the pixels immediately. Consume
      // the transient same-document entry without retaining an invisible viewer.
      if (historyActive && !closeRequested) {
        historyActive = false;
        ownerWindow.history.back();
      }
      releaseFocus();
    }
  };
}

export function OpsImagePreview({ artifact, onClose }: {
  artifact: OpsImageArtifactProjection;
  onClose: () => void;
}) {
  const [zoom, setZoom] = useState(1);
  const [maximized, setMaximized] = useState(false);
  const panelRef = useRef<HTMLElement>(null);
  const lifecycleRef = useRef<ReturnType<typeof installOpsImagePreviewLifecycle> | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const requestClose = () => {
    const lifecycle = lifecycleRef.current;
    if (lifecycle) lifecycle.requestClose();
    else closeRef.current();
  };

  useEffect(() => {
    const lifecycle = installOpsImagePreviewLifecycle(
      panelRef.current,
      artifact.artifactId,
      () => closeRef.current()
    );
    lifecycleRef.current = lifecycle;
    return () => {
      if (lifecycleRef.current === lifecycle) lifecycleRef.current = null;
      lifecycle.dispose();
    };
  }, [artifact.artifactId]);

  return <div
    className={`ops-image-preview${maximized ? " is-maximized" : ""}`}
    onPointerDown={(event) => { if (event.target === event.currentTarget) requestClose(); }}
    onKeyDown={(event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      requestClose();
    }}
  >
    <section className="ops-image-preview__panel" ref={panelRef} role="dialog" aria-modal="true" aria-label={`${artifact.altText} preview`} tabIndex={-1}>
      <header className="ops-image-preview__header">
        <button type="button" onClick={requestClose}><ArrowLeft size={17} aria-hidden="true" />Back</button>
        <strong>Image preview</strong>
        <button type="button" onClick={() => setMaximized((value) => !value)} aria-label={maximized ? "Restore preview size" : "Maximize preview"}>
          {maximized ? <Minimize2 size={18} aria-hidden="true" /> : <Maximize2 size={18} aria-hidden="true" />}
        </button>
        <button type="button" onClick={requestClose} aria-label="Close image preview"><X size={18} aria-hidden="true" /></button>
      </header>
      <div className="ops-image-preview__body">
        <div className={`ops-image-preview__canvas ops-image-preview__canvas--${zoom}`}>
          <img src={opsImageContentUrl(artifact.artifactId)} alt={artifact.altText} width={artifact.width} height={artifact.height} />
        </div>
      </div>
      <footer className="ops-image-preview__footer">
        <a href={opsImageContentUrl(artifact.artifactId, true)} download>Download image</a>
        <div className="ops-image-preview__zoom" aria-label="Image zoom">
          <button type="button" aria-label="Zoom out" disabled={zoom === 1} onClick={() => setZoom((value) => Math.max(1, value - 1))}><Minus size={17} aria-hidden="true" /></button>
          <button type="button" onClick={() => setZoom(1)}>Fit</button>
          <output aria-live="polite">{zoom === 1 ? "Fit" : `${zoom}×`}</output>
          <button type="button" aria-label="Zoom in" disabled={zoom === 4} onClick={() => setZoom((value) => Math.min(4, value + 1))}><Plus size={17} aria-hidden="true" /></button>
        </div>
      </footer>
    </section>
  </div>;
}

export function OpsImageArtifactCard({
  job: rawJob,
  onUpscale,
  upscalePending = null,
  notice = null,
  privateMode = false
}: {
  job: OpsImageJobProjection;
  onUpscale?: (artifactId: string, factor: 2 | 4) => void;
  upscalePending?: 2 | 4 | null;
  notice?: { text: string; tone: "status" | "error" } | null;
  privateMode?: boolean;
}) {
  const job = OpsImageJobProjectionSchema.parse(rawJob);
  const [previewOpen, setPreviewOpen] = useState(false);
  useEffect(() => {
    // Raster pixels cannot be phone-masked. A transition into Private must
    // clear the open state as well as unmount the visible preview, so leaving
    // Private never resurrects pixels the user did not reopen.
    if (privateMode) setPreviewOpen(false);
  }, [privateMode]);
  if (job.state === "PENDING" || job.state === "PROCESSING") {
    return <section className="ops-image-card ops-image-card--working" role="status"><p>{job.message}</p></section>;
  }
  if (job.state === "FAILED") {
    return (
      <section className="ops-image-card ops-image-card--failed" role="alert">
        <p>{job.message}</p>
        <small>Nothing was saved as a finished image.</small>
      </section>
    );
  }
  const artifact = job.artifact;
  return <>
    <figure className="ops-image-card ops-image-card--ready" data-image-artifact-id={artifact.artifactId}>
      {!privateMode ? <button
        className="ops-image-card__preview-trigger"
        type="button"
        aria-label={`Open image preview: ${artifact.altText}`}
        onClick={() => setPreviewOpen(true)}
      >
        <img
          src={opsImageContentUrl(artifact.artifactId)}
          alt={artifact.altText}
          width={artifact.width}
          height={artifact.height}
          loading="lazy"
          decoding="async"
        />
      </button> : <div className="ops-image-card__private"><span>Image hidden in Private mode.</span></div>}
      <figcaption>
        <p>{job.message}</p>
        <div className="ops-image-card__actions">
          <a href={opsImageContentUrl(artifact.artifactId, true)} download>Download image</a>
          {onUpscale ? artifact.availableUpscales.map((factor) => (
            <button type="button" key={factor} disabled={upscalePending !== null} onClick={() => onUpscale(artifact.artifactId, factor)}>
              {upscalePending === factor ? `Upscaling to ${factor}×…` : `Upscale ${factor}×`}
            </button>
          )) : null}
        </div>
        {upscalePending ? <p className="ops-image-card__notice" role="status">Upscaling to {upscalePending}×…</p> : null}
        {!upscalePending && notice ? <p className={`ops-image-card__notice ops-image-card__notice--${notice.tone}`} role={notice.tone === "error" ? "alert" : "status"}>{notice.text}</p> : null}
        <details>
          <summary>Image details</summary>
          <dl>
            <div><dt>Provider</dt><dd>{opsImageProviderLabel(artifact.provider)}</dd></div>
            <div><dt>Model</dt><dd>{artifact.model}</dd></div>
            <div><dt>Size</dt><dd>{artifact.width} × {artifact.height}</dd></div>
            <div><dt>SHA-256</dt><dd><code>{artifact.sha256}</code></dd></div>
            {artifact.providerRequestId ? <div><dt>Request</dt><dd><code>{artifact.providerRequestId}</code></dd></div> : null}
          </dl>
        </details>
      </figcaption>
    </figure>
    {!privateMode && previewOpen ? <OpsImagePreview artifact={artifact} onClose={() => setPreviewOpen(false)} /> : null}
  </>;
}
